
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { ManufacturerService } from '@/services/manufacturer.service'
import { Contact } from '@/@types/responses/contact/Contact'
import ContactModal from '@/components/modals/ContactModal.vue'
import Toaster from '@meforma/vue-toaster'
import { ManufacturerDetails } from '@/@types/responses/manufacturer/ManufacturerDetails'
import ContactTile from '@/components/elements/ContactTile.vue'
import { ContactService } from '@/services/contact.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import PhoneNumberInput from '@/components/shared/PhoneNumberInput.vue'
import { ManufacturerMessage } from '@/@types/messages/ManufacturerMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import Dropdown from '@/components/shared/Dropdown.vue'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { StateService } from '@/services/states.service'

export default defineComponent({
  name: 'Manufacturer',
  components: {
    AuditInformation,
    ContactModal,
    ContactTile,
    Dropdown,
    LoadingPulse,
    LockingButton,
    PhoneNumberInput,
    TextInput
  },
  beforeRouteLeave() {
    if (this.showContactModal) {
      this.showContactModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const contactService = inject(PROVIDER_CONSTANTS.contactService) as ContactService
    const manufacturerService = inject(PROVIDER_CONSTANTS.manufacturerService) as ManufacturerService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      contactService,
      manufacturerService,
      stateService,
      toast
    }
  },
  data() {
    let contactToEdit: Contact|undefined
    let manufacturer: ManufacturerDetails|undefined
    let unchangedManufacturer: ManufacturerDetails|undefined

    return {
      loading: true,
      manufacturer: manufacturer,
      unchangedManufacturer: unchangedManufacturer, 
      saving: false,
      showContactModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      contactToEdit
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.manufacturer
      const b = this.unchangedManufacturer

      return (a?.name !== b?.name ||
             a?.website !== b?.website ||
             a?.email !== b?.email ||
             a?.phoneNumber !== b?.phoneNumber ||
             a?.addressLine1 !== b?.addressLine1 ||
             a?.addressLine2 !== b?.addressLine2 ||
             a?.city !== b?.city ||
             a?.stateId !== b?.stateId ||
             a?.postalCode !== b?.postalCode) && a?.name.trim() !== ''
    },
    titleText(): string {
      return !this.manufacturer || this.manufacturer.id === 0 ? 'Create Manufacturer' : 'Edit Manufacturer'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    this.getStates().then(() => {
      if (this.id > 0) {
        this.getManufacturer()
      } else {
        this.manufacturer = new ManufacturerDetails(0)
        this.unchangedManufacturer = new ManufacturerDetails(0)
        this.loading = false
      }
    })
  },
  methods: {
    getManufacturer(): Promise<void> {
      return this.manufacturerService.getManufacturer(this.manufacturer ? this.manufacturer.id : this.id).then(manufacturer => {
        this.manufacturer = manufacturer
        this.unchangedManufacturer = JSON.parse(JSON.stringify(manufacturer))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the manufacturer. Try reloading the page.')
        }
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onCreateContactClicked(): void {
      this.contactToEdit = new Contact(0)
      this.showContactModal = true
    },
    onDeleteContact(contact: Contact): void {
      this.contactService.deleteContact(contact.id).then(() => {
        this.getManufacturer().then(() => {
          this.toast.success('The contact was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the contact. Please try again.')
        }
      })
    },
    onEditContact(contact: Contact): void {
      this.contactToEdit = contact
      this.showContactModal = true
    },
    onSaveManufacturerClicked(): void {
      if (!this.manufacturer) return

      this.saving = true
      
      const manufacturerMessage = new ManufacturerMessage(this.manufacturer.name, this.manufacturer.website, this.manufacturer.email, this.manufacturer.phoneNumber, this.manufacturer.addressLine1, this.manufacturer.addressLine2, this.manufacturer.city, this.manufacturer.stateId, this.manufacturer.postalCode)
      if (this.manufacturer.id === 0)
        this.manufacturerService.createManufacturer(manufacturerMessage).then(objectCreatedResponse => {
          if (!this.manufacturer) return

          this.manufacturer.id = objectCreatedResponse.id
          this.getManufacturer().then(() => {
            this.$router.replace(`/manufacturer/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The manufacturer was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the manufacturer. Please try again.')
          }
        })
      else
        this.manufacturerService.updateManufacturer(this.id, manufacturerMessage).then(() => {
          this.toast.success('The manufacturer was updated successfully.')
          this.saving = false
          this.$router.push('/manufacturers')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the manufacturer. Please try again.')
          }
        })
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.manufacturer)
        document.title = `${this.manufacturer.name} - Short Action Customs, LLC`
    }
  }
})
